import React from 'react';
import { Globe, Workflow, Edit3, Navigation, Code } from 'lucide-react';
import { ActionConfig, ActionType } from '../../../services/queries/buttonActionQueries';

interface ActionConfiguratorProps {
  action: ActionConfig;
  availableFields: any[];
  onChange: (action: ActionConfig) => void;
}

export const ActionConfigurator: React.FC<ActionConfiguratorProps> = ({
  action,
  availableFields,
  onChange
}) => {
  const handleTypeChange = (type: ActionType) => {
    // Reset config when type changes
    onChange({
      type,
      apiUrl: undefined,
      apiMethod: undefined,
      apiHeaders: undefined,
      apiBody: undefined,
      workflowId: undefined,
      fieldUpdates: undefined,
      navigateUrl: undefined,
      openInNewTab: undefined,
      javascriptCode: undefined
    });
  };

  return (
    <div className="space-y-4">
      {/* Action Type Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Action Type <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[
            { type: 'call-api' as ActionType, label: 'Call API', icon: Globe },
            { type: 'run-workflow' as ActionType, label: 'Run Workflow', icon: Workflow },
            { type: 'update-fields' as ActionType, label: 'Update Fields', icon: Edit3 },
            { type: 'navigate' as ActionType, label: 'Navigate', icon: Navigation },
            { type: 'javascript' as ActionType, label: 'JavaScript', icon: Code }
          ].map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              onClick={() => handleTypeChange(type)}
              className={`p-3 rounded-lg border-2 transition-all text-center ${
                action.type === type
                  ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600'
              }`}
            >
              <Icon className={`w-5 h-5 mx-auto mb-1 ${
                action.type === type
                  ? 'text-purple-600 dark:text-purple-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`} />
              <span className={`text-xs font-medium ${
                action.type === type
                  ? 'text-purple-900 dark:text-purple-300'
                  : 'text-gray-700 dark:text-gray-300'
              }`}>
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Action-Specific Configuration */}
      {action.type === 'call-api' && (
        <CallApiConfig action={action} onChange={onChange} />
      )}

      {action.type === 'run-workflow' && (
        <RunWorkflowConfig action={action} onChange={onChange} />
      )}

      {action.type === 'update-fields' && (
        <UpdateFieldsConfig action={action} availableFields={availableFields} onChange={onChange} />
      )}

      {action.type === 'navigate' && (
        <NavigateConfig action={action} onChange={onChange} />
      )}

      {action.type === 'javascript' && (
        <JavaScriptConfig action={action} onChange={onChange} />
      )}
    </div>
  );
};

// Call API Configuration
const CallApiConfig: React.FC<{ action: ActionConfig; onChange: (action: ActionConfig) => void }> = ({
  action,
  onChange
}) => (
  <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">API Configuration</h4>

    <div className="grid grid-cols-4 gap-3">
      <div className="col-span-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Method
        </label>
        <select
          value={action.apiMethod || 'POST'}
          onChange={(e) => onChange({ ...action, apiMethod: e.target.value as any })}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="DELETE">DELETE</option>
        </select>
      </div>

      <div className="col-span-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          API URL <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={action.apiUrl || ''}
          onChange={(e) => onChange({ ...action, apiUrl: e.target.value })}
          placeholder="https://api.example.com/endpoint"
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Request Body (JSON)
      </label>
      <textarea
        value={action.apiBody || ''}
        onChange={(e) => onChange({ ...action, apiBody: e.target.value })}
        placeholder={'{\n  "recordId": "{id}",\n  "field": "{fieldName}"\n}'}
        rows={6}
        className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      />
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Use {'{id}'} for record ID, {'{fieldName}'} for field values
      </p>
    </div>
  </div>
);

// Run Workflow Configuration
const RunWorkflowConfig: React.FC<{ action: ActionConfig; onChange: (action: ActionConfig) => void }> = ({
  action,
  onChange
}) => (
  <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Workflow Configuration</h4>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Select Workflow <span className="text-red-500">*</span>
      </label>
      <select
        value={action.workflowId || ''}
        onChange={(e) => onChange({ ...action, workflowId: e.target.value })}
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      >
        <option value="">Select a workflow...</option>
        <option value="workflow_001">Send Welcome Email Workflow</option>
        <option value="workflow_002">Update Client Status Workflow</option>
        <option value="workflow_003">Create Follow-up Task Workflow</option>
      </select>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        The workflow will execute with the current record context
      </p>
    </div>
  </div>
);

// Update Fields Configuration
const UpdateFieldsConfig: React.FC<{
  action: ActionConfig;
  availableFields: any[];
  onChange: (action: ActionConfig) => void
}> = ({ action, availableFields, onChange }) => {
  const handleAddFieldUpdate = () => {
    const firstField = availableFields[0];
    if (!firstField) return;

    const newUpdate = {
      fieldId: firstField.id,
      fieldLabel: firstField.label,
      value: ''
    };

    onChange({
      ...action,
      fieldUpdates: [...(action.fieldUpdates || []), newUpdate]
    });
  };

  const handleUpdateField = (index: number, updates: Partial<{ fieldId: string; fieldLabel: string; value: string | number | boolean }>) => {
    const fieldUpdates = [...(action.fieldUpdates || [])];
    fieldUpdates[index] = { ...fieldUpdates[index], ...updates };
    onChange({ ...action, fieldUpdates });
  };

  const handleRemoveField = (index: number) => {
    onChange({
      ...action,
      fieldUpdates: action.fieldUpdates?.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Field Updates</h4>
        <button
          onClick={handleAddFieldUpdate}
          className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
        >
          + Add Field
        </button>
      </div>

      {action.fieldUpdates && action.fieldUpdates.length > 0 ? (
        <div className="space-y-3">
          {action.fieldUpdates.map((update, index) => {
            const field = availableFields.find(f => f.id === update.fieldId);

            return (
              <div key={index} className="flex items-start gap-2 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Field</label>
                    <select
                      value={update.fieldId}
                      onChange={(e) => {
                        const selectedField = availableFields.find(f => f.id === e.target.value);
                        if (selectedField) {
                          handleUpdateField(index, {
                            fieldId: selectedField.id,
                            fieldLabel: selectedField.label,
                            value: ''
                          });
                        }
                      }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {availableFields.map(f => (
                        <option key={f.id} value={f.id}>{f.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">New Value</label>
                    {field?.type === 'checkbox' ? (
                      <select
                        value={String(update.value)}
                        onChange={(e) => handleUpdateField(index, { value: e.target.value === 'true' })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    ) : field?.type === 'picklist' ? (
                      <select
                        value={String(update.value)}
                        onChange={(e) => handleUpdateField(index, { value: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Select value...</option>
                        {field.options?.map((opt: any) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field?.type === 'number' || field?.type === 'currency' ? 'number' : 'text'}
                        value={String(update.value)}
                        onChange={(e) => handleUpdateField(index, { value: e.target.value })}
                        placeholder="Enter value..."
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleRemoveField(index)}
                  className="mt-5 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
          No field updates configured. Click "Add Field" to get started.
        </div>
      )}
    </div>
  );
};

// Navigate Configuration
const NavigateConfig: React.FC<{ action: ActionConfig; onChange: (action: ActionConfig) => void }> = ({
  action,
  onChange
}) => (
  <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Navigation Configuration</h4>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Navigate To URL <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={action.navigateUrl || ''}
        onChange={(e) => onChange({ ...action, navigateUrl: e.target.value })}
        placeholder="/page/{id} or https://external.com"
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      />
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Use {'{id}'} to insert the record ID in the URL
      </p>
    </div>

    <div>
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={action.openInNewTab || false}
          onChange={(e) => onChange({ ...action, openInNewTab: e.target.checked })}
          className="w-4 h-4 text-purple-600 rounded"
        />
        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
          Open in new tab
        </span>
      </label>
    </div>
  </div>
);

// JavaScript Configuration
const JavaScriptConfig: React.FC<{ action: ActionConfig; onChange: (action: ActionConfig) => void }> = ({
  action,
  onChange
}) => (
  <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">JavaScript Code</h4>

    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        JavaScript Code <span className="text-red-500">*</span>
      </label>
      <textarea
        value={action.javascriptCode || ''}
        onChange={(e) => onChange({ ...action, javascriptCode: e.target.value })}
        placeholder={'// Record data is available as "record"\nconsole.log(record.id);\n\n// Return true for success, false for error\nreturn true;'}
        rows={10}
        className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      />
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        The record object is available as a variable. Return true for success, false for error.
      </p>
    </div>

    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3">
      <p className="text-xs text-yellow-800 dark:text-yellow-300">
        <strong>⚠️ Warning:</strong> Custom JavaScript can access sensitive data. Only use trusted code.
      </p>
    </div>
  </div>
);
